import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './LeadMagnet.css';

// ─────────────────────────────────────────────────────────────
//  Kundenmagnet · "Magnetic Treasury"
//
//  Zwei Unter-Ansichten:
//   1) Lead Magnet Research (Konkurrenz-Audit) – NUR fuer den Admin sichtbar &
//      ausloesbar. Weltweiter KI-Audit, der zeigt, welche Lead-Magneten/Offers die
//      Konkurrenz in DE & international einsetzt, bewertet nach Komplexitaet,
//      Kosten, Wirksamkeit & Uniqueness, plus 6 ueberholte Konzepte.
//   2) Kundenmagnete – die fuer DIESEN Kunden abgelegten Lead-Magneten (Deliverable),
//      gross praesentiert (max 2 nebeneinander), mit Datei-Upload, Tags & Infos.
//
//  Gesperrt (kein Zusatzpaket): der Kunde sieht einen edlen Locked-Screen, der ihn
//  heiss auf das Upgrade macht. Der Admin sieht trotz Sperre den vollen Editor
//  (mit Hinweis, dass noch nicht freigeschaltet ist). Freigeschaltet wird AUSSCHLIESSLICH
//  ueber das Team Dashboard.
// ─────────────────────────────────────────────────────────────

const makeMagnetId = () => `lm-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

const isImageType = (t) => typeof t === 'string' && t.toLowerCase().startsWith('image/');

// Die 5 Kern-Achsen, einheitlich fuer Markt-Scan-Ideen UND engineerte Offers.
// 'effort' (Aufwand) ist die einzige Achse, bei der "niedrig = besser" gilt
// (daher cost-Tonalitaet).
const CORE_AXES = [
  { key: 'effectiveness', label: 'Wirksamkeit', tone: 'good' },
  { key: 'productFit', label: 'Produkt-Fit', tone: 'good' },
  { key: 'autonomy', label: 'Eigenständigkeit', tone: 'good' },
  { key: 'uniqueness', label: 'Uniqueness', tone: 'good' },
  { key: 'effort', label: 'Aufwand', tone: 'cost' },
];

// Hinweis: Der frühere „Offer-Discovery"-Feinschliff (manuelle Zusatzinfos) wurde
// entfernt. Die Engine arbeitet ausschließlich mit den bereits abgelegten Research-
// und Zielgruppen-Daten – es wird hier nichts mehr manuell eingegeben.

const REGION_LABEL = { DE: 'Deutschland', International: 'International', Global: 'Global' };

// Klartext-Labels fuer fehlende Offer-Foundations (Enforcement-Warnung im Lab).
const FOUNDATION_LABEL = {
  focus: 'Fokus (1 Person, 1 Problem)',
  tangibleResult: 'Greifbares Ergebnis',
  timeline: 'Zeitrahmen',
  polarising: 'Polarisierung',
  guarantee: 'Garantie',
};

// Welche Foundations sind kundenseitig editierbar (im Magnet-Board sichtbar/aenderbar)
// und welche bleiben rein intern (landen in den Team-Notizen). Wichtig fuer die
// Warnungen: nur kundenseitige Luecken lassen sich im UI direkt schliessen, interne
// werden separat und entschaerft angezeigt, statt zu einem "kann ich eh nicht fixen".
const CUSTOMER_FOUNDATION_KEYS = ['tangibleResult', 'timeline', 'guarantee'];
const INTERNAL_FOUNDATION_KEYS = ['focus', 'polarising'];

// Welche kundenrelevanten Bausteine fehlen an einem bereits uebernommenen Magnet noch?
// Greift auf das strukturierte foundations-Objekt zu (riskReversal ist Bonus, nicht Pflicht).
const magnetFoundationsMissing = (m) => {
  const f = (m && m.foundations) || {};
  return CUSTOMER_FOUNDATION_KEYS.filter((k) => !((f[k] || '').trim()));
};

// Phasen-Feedback waehrend des (60–150 s langen) Engine-Laufs. Die Cloud Function
// liefert kein Live-Streaming, daher takten wir die Phasen zeitbasiert durch – das
// nimmt dem Admin die Unsicherheit, ob noch etwas passiert (statt nur ein Spinner).
const RUN_PHASES = [
  'Kontext & Offer-Discovery werden ausgewertet…',
  'Live-Recherche: was läuft aktuell bei der Konkurrenz…',
  'Maßgeschneiderte Einstiegsangebote werden engineert…',
  'Bewertung auf 5 Achsen & Scoring…',
  'Feinschliff der Empfehlung, gleich fertig…',
];

// ── Gesamt-Score-Badge (0–100) ──
const ScoreBadge = ({ score }) => {
  const s = Math.max(0, Math.min(100, Math.round(Number(score) || 0)));
  const tier = s >= 80 ? 'top' : s >= 60 ? 'good' : 'mid';
  return (
    <span className={`lm-score lm-score-${tier}`} title="Gewichteter Gesamt-Score (0–100)">
      <span className="lm-score-num">{s}</span>
      <span className="lm-score-unit">/100</span>
    </span>
  );
};

// ── Aufklappbarer Details-Bereich (haelt die Karten im Lab schlank) ──
const DetailsToggle = ({ open, onToggle }) => (
  <button type="button" className="lm-details-toggle" onClick={onToggle} aria-expanded={open}>
    <span>{open ? 'Details ausblenden' : 'Details ansehen'}</span>
    <svg className={`lm-details-chev ${open ? 'is-open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  </button>
);

// ── Warenkorb-Button (Toggle): legt das Konzept in die Auswahl / nimmt es heraus ──
const CartToggle = ({ inCart, onToggle, variant = 'gold' }) => (
  <button
    type="button"
    className={`lm-btn lm-cart-toggle ${inCart ? 'is-incart' : (variant === 'ghost' ? 'lm-btn-ghost' : 'lm-btn-gold')}`}
    onClick={onToggle}
  >
    {inCart ? '✓ Zum Magnet Board hinzugefügt' : '+ Zum Magnet Board hinzufügen'}
  </button>
);

// ── Alternative Titel-Varianten (Hormozi-Style) unter der Haupt-Headline ──
const TitleAlternatives = ({ variants, primary }) => {
  const alts = (Array.isArray(variants) ? variants : []).filter((v) => v && v !== primary);
  if (alts.length === 0) return null;
  return (
    <div className="lm-idea-alts">
      <span className="lm-idea-alts-label">Alternativen</span>
      {alts.map((v, i) => <span key={i} className="lm-idea-alt">{v}</span>)}
    </div>
  );
};

// ── Kern-Bausteine eines Angebots gut sichtbar (statt im Detail-Aufklapper): das
// greifbare Ergebnis, der Zeitrahmen und die Garantie. Genau die Hormozi-Foundations,
// die den No-Brainer ausmachen, direkt auf der Kartenoberflaeche. ──
const OfferHighlights = ({ offer }) => {
  const result = (offer.tangibleResult || '').trim();
  const time = (offer.timeline || '').trim();
  const guarantee = (offer.guarantee || '').trim();
  if (!result && !time && !guarantee) return null;
  return (
    <div className="lm-offer-highlights">
      {result && (
        <div className="lm-offer-hl">
          <span className="lm-offer-hl-label">Ergebnis</span>
          <p>{result}</p>
        </div>
      )}
      {time && (
        <div className="lm-offer-hl">
          <span className="lm-offer-hl-label">Zeitrahmen</span>
          <p>{time}</p>
        </div>
      )}
      {guarantee && (
        <div className="lm-offer-hl lm-offer-hl-guarantee">
          <span className="lm-offer-hl-label">Garantie</span>
          <p>{guarantee}</p>
        </div>
      )}
    </div>
  );
};

// ── Ein engineertes Einstiegsangebot (Phase 2) ──
const OfferCard = ({ offer, rank, inCart, onToggleCart }) => {
  const [open, setOpen] = useState(false);
  const hasDetails = offer.coldOutreachAngle || offer.focus || offer.polarising
    || offer.riskReversal || offer.alignment || offer.clientRequirements
    || offer.coreFitNote || offer.rationale || offer.implementation || (Array.isArray(offer.techTools) && offer.techTools.length > 0);
  return (
    <article className={`lm-idea lm-offer ${offer.recommended ? 'is-best' : ''} ${inCart ? 'is-incart' : ''}`}>
      {offer.recommended && <span className="lm-offer-best">Stärkstes Konzept</span>}
      <div className="lm-idea-top">
        <span className="lm-idea-rank">{String(rank).padStart(2, '0')}</span>
        <div className="lm-idea-titlewrap">
          <h4 className="lm-idea-title">{offer.name}</h4>
          <TitleAlternatives variants={offer.nameVariants} primary={offer.name} />
          <div className="lm-idea-badges">
            {offer.format && <span className="lm-badge lm-badge-format">{offer.format}</span>}
            <span className={`lm-badge lm-badge-${offer.delivery === 'dfy' ? 'dfy' : 'ideas'}`}>
              {offer.delivery === 'dfy' ? 'Done-for-you' : 'Ideas only'}
            </span>
          </div>
        </div>
        <ScoreBadge score={offer.score} />
      </div>

      {offer.promise && <p className="lm-offer-promise">{offer.promise}</p>}
      {offer.description && <p className="lm-idea-desc">{offer.description}</p>}

      <OfferHighlights offer={offer} />

      {Array.isArray(offer.foundationsMissing) && offer.foundationsMissing.length > 0 && (() => {
        const customerMissing = offer.foundationsMissing.filter((k) => CUSTOMER_FOUNDATION_KEYS.includes(k));
        const internalMissing = offer.foundationsMissing.filter((k) => INTERNAL_FOUNDATION_KEYS.includes(k));
        const join = (arr) => arr.map((k) => FOUNDATION_LABEL[k] || k).join(', ');
        return (
          <div className="lm-foundations-warn" title="Fehlende Offer-Bausteine. Kunden-Bausteine ergänzt du beim Übernehmen im Magnet Board, interne in der Team-Notiz.">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
            <span>
              {customerMissing.length > 0 && <>Kunden-Bausteine fehlen: {join(customerMissing)}</>}
              {customerMissing.length > 0 && internalMissing.length > 0 && ' · '}
              {internalMissing.length > 0 && <span className="lm-foundations-warn-intern">intern offen: {join(internalMissing)}</span>}
            </span>
          </div>
        );
      })()}

      <div className="lm-idea-ratings lm-offer-ratings">
        {CORE_AXES.map((m) => <RatingBar key={m.key} label={m.label} value={offer[m.key]} tone={m.tone} />)}
      </div>

      {hasDetails && <DetailsToggle open={open} onToggle={() => setOpen((v) => !v)} />}
      {hasDetails && open && (
        <div className="lm-idea-details">
          {offer.coldOutreachAngle && (
            <div className="lm-idea-block lm-idea-block-angle">
              <span className="lm-idea-block-label">Cold-Outreach-Angle</span>
              <p>{offer.coldOutreachAngle}</p>
            </div>
          )}
          {offer.focus && (
            <div className="lm-idea-block">
              <span className="lm-idea-block-label">Fokus (1 Person, 1 Problem)</span>
              <p>{offer.focus}</p>
            </div>
          )}
          {offer.polarising && (
            <div className="lm-idea-block">
              <span className="lm-idea-block-label">Polarisierung (zieht an / stößt ab)</span>
              <p>{offer.polarising}</p>
            </div>
          )}
          {offer.riskReversal && (
            <div className="lm-idea-block">
              <span className="lm-idea-block-label">Risiko-Umkehr</span>
              <p>{offer.riskReversal}</p>
            </div>
          )}
          {offer.alignment && (
            <div className="lm-idea-block">
              <span className="lm-idea-block-label">Aligned (beide gewinnen)</span>
              <p>{offer.alignment}</p>
            </div>
          )}
          {offer.clientRequirements && (
            <div className="lm-idea-block">
              <span className="lm-idea-block-label">Was der Kunde tun muss</span>
              <p>{offer.clientRequirements}</p>
            </div>
          )}
          {offer.coreFitNote && (
            <div className="lm-idea-block lm-idea-block-angle">
              <span className="lm-idea-block-label">Brücke zur Hauptleistung</span>
              <p>{offer.coreFitNote}</p>
            </div>
          )}
          {offer.rationale && (
            <div className="lm-idea-block">
              <span className="lm-idea-block-label">Wert-zu-Kosten</span>
              <p>{offer.rationale}</p>
            </div>
          )}
          {offer.implementation && (
            <div className="lm-idea-block">
              <span className="lm-idea-block-label">Umsetzung</span>
              <p>{offer.implementation}</p>
            </div>
          )}
          {Array.isArray(offer.techTools) && offer.techTools.length > 0 && (
            <div className="lm-idea-tools">
              <span className="lm-idea-block-label">Tech &amp; KI</span>
              <div className="lm-idea-tools-chips">
                {offer.techTools.map((t, i) => <span key={i} className="lm-tool-chip">{t}</span>)}
              </div>
            </div>
          )}
        </div>
      )}

      {onToggleCart && <CartToggle inCart={inCart} onToggle={onToggleCart} variant="gold" />}
    </article>
  );
};

// ── Kleine Bewertungs-Leiste (1–10) ──
const RatingBar = ({ label, value, tone }) => {
  const v = Math.max(1, Math.min(10, Number(value) || 0));
  return (
    <div className="lm-rating">
      <span className="lm-rating-label">{label}</span>
      <span className={`lm-rating-track lm-rating-${tone}`}>
        <span className="lm-rating-fill" style={{ width: `${v * 10}%` }} />
      </span>
      <span className="lm-rating-num">{v}</span>
    </div>
  );
};

// ── Eine "heiße" Audit-Idee ──
const HotIdeaCard = ({ idea, rank, inCart, onToggleCart }) => {
  const [open, setOpen] = useState(false);
  const hasDetails = idea.whyItWorks || idea.coldOutreachAngle || idea.implementation || (Array.isArray(idea.techTools) && idea.techTools.length > 0);
  return (
    <article className={`lm-idea ${inCart ? 'is-incart' : ''}`}>
      <div className="lm-idea-top">
        <span className="lm-idea-rank">{String(rank).padStart(2, '0')}</span>
        <div className="lm-idea-titlewrap">
          <h4 className="lm-idea-title">{idea.title}</h4>
          <TitleAlternatives variants={idea.titleVariants} primary={idea.title} />
          <div className="lm-idea-badges">
            {idea.format && <span className="lm-badge lm-badge-format">{idea.format}</span>}
            {idea.region && <span className="lm-badge lm-badge-region">{REGION_LABEL[idea.region] || idea.region}</span>}
            <span className={`lm-badge lm-badge-${idea.delivery === 'dfy' ? 'dfy' : 'ideas'}`}>
              {idea.delivery === 'dfy' ? 'Done-for-you' : 'Ideas only'}
            </span>
          </div>
        </div>
      </div>

      {idea.description && <p className="lm-idea-desc">{idea.description}</p>}

      <div className="lm-idea-ratings">
        {CORE_AXES.map((m) => (
          <RatingBar key={m.key} label={m.label} value={idea[m.key]} tone={m.tone} />
        ))}
      </div>

      {hasDetails && <DetailsToggle open={open} onToggle={() => setOpen((v) => !v)} />}
      {hasDetails && open && (
        <div className="lm-idea-details">
          {idea.whyItWorks && (
            <div className="lm-idea-block">
              <span className="lm-idea-block-label">Warum es zieht</span>
              <p>{idea.whyItWorks}</p>
            </div>
          )}
          {idea.coldOutreachAngle && (
            <div className="lm-idea-block lm-idea-block-angle">
              <span className="lm-idea-block-label">Cold-Outreach-Angle</span>
              <p>{idea.coldOutreachAngle}</p>
            </div>
          )}
          {idea.implementation && (
            <div className="lm-idea-block">
              <span className="lm-idea-block-label">Umsetzung</span>
              <p>{idea.implementation}</p>
            </div>
          )}
          {Array.isArray(idea.techTools) && idea.techTools.length > 0 && (
            <div className="lm-idea-tools">
              <span className="lm-idea-block-label">Tech & KI</span>
              <div className="lm-idea-tools-chips">
                {idea.techTools.map((t, i) => <span key={i} className="lm-tool-chip">{t}</span>)}
              </div>
            </div>
          )}
        </div>
      )}

      {onToggleCart && <CartToggle inCart={inCart} onToggle={onToggleCart} variant="ghost" />}
    </article>
  );
};

// ── Warenkorb-Toggle für den Brain Breaker ──
const BrainBreakerPromote = ({ inCart, onToggle }) => (
  <button type="button" className={`lm-btn lm-cart-toggle lm-brainbreaker-promote ${inCart ? 'is-incart' : 'lm-btn-gold'}`} onClick={onToggle}>
    {inCart ? '✓ Zum Magnet Board hinzugefügt' : '+ Zum Magnet Board hinzufügen'}
  </button>
);

// ── Persona-Strip: spiegelt die Kunden-Psychologie (aus dem Audience-Tab) kompakt
// ins Magnet Lab, damit das Team beim Generieren/Bewerten den Zielkunden + dessen
// groessten Pain/Wunsch direkt vor Augen hat (Richtschnur fuer die Offers). Read-only.
const PersonaStrip = ({ psychology }) => {
  const cp = psychology || {};
  const persona = (cp.persona || '').trim();
  const problem = (cp.biggestProblem || '').trim();
  const result = (cp.biggestResult || '').trim();
  if (!persona && !problem && !result) return null;
  return (
    <div className="lm-persona" title="Aus der Kunden-Psychologie (Audience-Tab). Richtschnur für die Bewertung der Angebote.">
      <span className="lm-persona-eyebrow">Zielkunde im Fokus</span>
      {persona && <p className="lm-persona-who">{persona}</p>}
      {(problem || result) && (
        <div className="lm-persona-pg">
          {problem && (
            <div className="lm-persona-cell lm-persona-pain">
              <span className="lm-persona-cell-label">Größtes Problem</span>
              <p>{problem}</p>
            </div>
          )}
          {result && (
            <div className="lm-persona-cell lm-persona-gain">
              <span className="lm-persona-cell-label">Gewünschtes Ergebnis</span>
              <p>{result}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── Magnet Lab: Lead Magnet Research & Offer-Engineering (Admin) ──
const AuditView = ({ audit, generateAudit, generateDirections, isAdmin, unlocked = false, cartIds, onToggleCart, customerPsychology = null }) => {
  const [busy, setBusy] = useState(false);
  const [busySection, setBusySection] = useState('all'); // 'all' | 'scan' | 'offers'
  const [err, setErr] = useState('');
  const [phaseIdx, setPhaseIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  // Popup vor dem Voll-Lauf: optionaler Freitext, mit dem das Team der KI Richtung/Ideen
  // mitgibt (z. B. "kein PDF, eher Richtung interaktive Tools"). Leer lassen ist erlaubt.
  const [promptOpen, setPromptOpen] = useState(false);
  const [guidance, setGuidance] = useState('');
  // KI-Richtungsvorschlaege: das Team laesst sich Richtungen vorschlagen, waehlt Favoriten,
  // und GENAU die steuern dann die Generierung (stark gewichtet, nicht nur ein Freitext-Hauch).
  const [directions, setDirections] = useState([]);
  const [dirBusy, setDirBusy] = useState(false);
  const [dirErr, setDirErr] = useState('');
  const [selectedDirs, setSelectedDirs] = useState(() => new Set());
  const pendingSectionsRef = useRef(null);

  // sections = undefined -> beide; ['scan'] / ['offers'] -> nur diese Sektion neu erzeugen.
  // runGuidance = optionaler Freitext; runDirections = vom Team gewaehlte Richtungen (stark
  // gewichtete Steuerung). Sonst nutzt die Engine die abgelegten Research-/Zielgruppen-Daten.
  const run = async (sections, runGuidance, runDirections) => {
    if (busy || !generateAudit) return;
    setBusySection(Array.isArray(sections) && sections.length === 1 ? sections[0] : 'all');
    setBusy(true);
    setErr('');
    try {
      const g = (runGuidance || '').trim();
      const dirs = Array.isArray(runDirections) ? runDirections.filter(Boolean) : [];
      const discovery = {};
      if (g) discovery.guidance = g;
      if (dirs.length) discovery.directions = dirs;
      await generateAudit(Object.keys(discovery).length ? discovery : undefined, sections);
    } catch (e) {
      setErr(e.message || 'Audit fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const loadDirections = useCallback(async () => {
    if (!generateDirections || dirBusy) return;
    setDirBusy(true);
    setDirErr('');
    try {
      const list = await generateDirections();
      setDirections(Array.isArray(list) ? list : []);
      setSelectedDirs(new Set());
    } catch (e) {
      setDirErr(e.message || 'Richtungsvorschläge fehlgeschlagen.');
    } finally {
      setDirBusy(false);
    }
  }, [generateDirections, dirBusy]);

  const toggleDir = (idx) => {
    setSelectedDirs((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx); else next.add(idx);
      return next;
    });
  };

  // Statt direkt zu starten erst das Popup öffnen (für die Voll-Läufe). sections wird
  // gemerkt, bis das Team bestätigt – mit oder ohne Zusatz-Infos/Richtungen.
  const openPrompt = (sections) => {
    if (busy || !generateAudit) return;
    pendingSectionsRef.current = sections || undefined;
    setPromptOpen(true);
    // Richtungen einmalig automatisch vorladen (guenstig/schnell), wenn noch keine da sind.
    if (generateDirections && directions.length === 0 && !dirBusy) loadDirections();
  };

  // Ausgewaehlte Richtungen als kompakte Steuer-Strings (Titel + kurze Beschreibung).
  const selectedDirectionStrings = () => Array.from(selectedDirs)
    .map((i) => directions[i])
    .filter(Boolean)
    .map((d) => (d.description ? `${d.title}: ${d.description}` : d.title));

  const startRun = (runGuidance, withDirections) => {
    const sections = pendingSectionsRef.current;
    const dirs = withDirections ? selectedDirectionStrings() : [];
    setPromptOpen(false);
    run(sections, runGuidance, dirs);
  };

  // Phasen + Fortschritt zeitbasiert durchtakten, solange die Engine laeuft. Der
  // Fortschritt naehert sich asymptotisch ~92% (ease-out), damit es nie wirkt, als
  // haenge er bei 100%, bevor das Ergebnis wirklich da ist.
  useEffect(() => {
    if (!busy) { setProgress(0); setPhaseIdx(0); return undefined; }
    setProgress(6); setPhaseIdx(0);
    const startedAt = Date.now();
    const id = setInterval(() => {
      const elapsed = (Date.now() - startedAt) / 1000;
      setProgress(Math.min(92, Math.round(92 * (1 - Math.exp(-elapsed / 55)))));
      setPhaseIdx(Math.min(RUN_PHASES.length - 1, Math.floor(elapsed / 13)));
    }, 700);
    return () => clearInterval(id);
  }, [busy]);

  const hot = (audit && Array.isArray(audit.hotIdeas)) ? audit.hotIdeas : [];
  const outdated = (audit && Array.isArray(audit.outdatedIdeas)) ? audit.outdatedIdeas : [];
  const offers = (audit && Array.isArray(audit.engineeredOffers)) ? audit.engineeredOffers : [];
  const hasContent = hot.length > 0 || outdated.length > 0 || offers.length > 0;
  const generatedAt = audit && audit.generatedAt
    ? new Date(audit.generatedAt).toLocaleString('de-DE', { dateStyle: 'medium', timeStyle: 'short' })
    : null;

  // Psychologie kommt entweder frisch mit dem Audit (Backfill im Lab) oder aus dem
  // bereits gespeicherten Report. Frisches Audit hat Vorrang, damit der Strip direkt
  // nach dem Generieren erscheint (ohne Reload).
  const psychology = (audit && audit.customerPsychology) || customerPsychology;

  return (
    <div className="lm-audit">
      <PersonaStrip psychology={psychology} />
      {hasContent ? (
        <div className="lm-audit-head">
          <div className="lm-audit-head-text">
            <span className="lm-eyebrow">Nur für das Team · vertraulich</span>
            <h3 className="lm-audit-title">Lead Magnet Research &amp; Offer-Engineering</h3>
          </div>
          {isAdmin && (
            <button className={`lm-btn lm-btn-gold ${busy ? 'is-busy' : ''}`} onClick={() => openPrompt()} disabled={busy}>
              {busy && busySection === 'all' ? (
                <><span className="lm-spin" />Engine läuft…</>
              ) : 'Alles neu generieren'}
            </button>
          )}
        </div>
      ) : (!busy && !err && (
        <div className="lm-launch">
          <div className={`lm-launch-card ${unlocked ? '' : 'is-locked'}`}>
            <span className="lm-launch-emblem" aria-hidden="true">
              <svg width="30" height="30" viewBox="0 0 64 64" fill="currentColor">
                <path d="M60,28.5H45.062L58,21.03a3.5,3.5,0,0,0,1.281-4.78h0a3.5,3.5,0,0,0-4.78-1.281L41.562,22.438,49.031,9.5a3.5,3.5,0,0,0-1.281-4.78h0A3.5,3.5,0,0,0,42.969,6L35.5,18.938V4A3.5,3.5,0,0,0,32,.5h0A3.5,3.5,0,0,0,28.5,4V18.938L21.031,6a3.5,3.5,0,0,0-4.78-1.281h0A3.5,3.5,0,0,0,14.969,9.5l7.468,12.937L9.5,14.969A3.5,3.5,0,0,0,4.72,16.25h0A3.5,3.5,0,0,0,6,21.03L18.938,28.5H4A3.5,3.5,0,0,0,.5,32v0A3.5,3.5,0,0,0,4,35.5H18.938L6,42.97A3.5,3.5,0,0,0,4.72,47.75h0A3.5,3.5,0,0,0,9.5,49.031l12.937-7.468L14.969,54.5a3.5,3.5,0,0,0,1.281,4.78h0A3.5,3.5,0,0,0,21.031,58L28.5,45.063V60A3.5,3.5,0,0,0,32,63.5h0A3.5,3.5,0,0,0,35.5,60V45.063L42.969,58a3.5,3.5,0,0,0,4.78,1.281h0a3.5,3.5,0,0,0,1.281-4.78L41.562,41.563,54.5,49.031a3.5,3.5,0,0,0,4.78-1.281h0A3.5,3.5,0,0,0,58,42.97L45.062,35.5H60A3.5,3.5,0,0,0,63.5,32v0A3.5,3.5,0,0,0,60,28.5Z" />
              </svg>
            </span>
            <span className="lm-launch-step">Schritt 1</span>
            <h3 className="lm-launch-heading">„No-Brainer"-Angebot kreieren</h3>
            <p className="lm-launch-tag">
              Hier beginnt alles. Wir finden heraus, welche Angebote gerade am Markt platziert
              werden und was davon wirklich zieht. Daraus filtern wir die echten No-Brainer
              heraus, die deine Zielkunden sofort überzeugen, und machen daraus die Grundlage
              für unwiderstehliche Einstiegsangebote.
            </p>
            {unlocked ? (
              <button type="button" className="lm-launch-btn" onClick={() => openPrompt()} disabled={busy || !isAdmin}>
                <span className="lm-launch-label">Analyse starten</span>
              </button>
            ) : (
              <button type="button" className="lm-launch-btn lm-launch-btn-locked" disabled title="Im Team Dashboard freischalten">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="4" y="10.5" width="16" height="10" rx="2" />
                  <path d="M8 10.5V7a4 4 0 0 1 8 0v3.5" />
                </svg>
                <span className="lm-launch-label">Exklusiv im Premium Setup</span>
              </button>
            )}
          </div>
        </div>
      ))}

      {err && <div className="lm-audit-err">{err}</div>}
      {!err && audit && audit.partial && hasContent && !busy && (
        <div className="lm-audit-warn">
          Hinweis: Eine der beiden Engines hat diesmal nichts geliefert. Du siehst ein Teilergebnis. Starte neu, um den fehlenden Teil zu ergänzen.
        </div>
      )}
      {generatedAt && !busy && <div className="lm-audit-meta">Zuletzt erstellt: {generatedAt}</div>}

      {busy && (
        <div className="lm-run" role="status" aria-live="polite">
          <div className="lm-run-orbit"><span className="lm-run-orbit-core" /></div>
          <div className="lm-run-body">
            <span className="lm-run-phase">{RUN_PHASES[phaseIdx]}</span>
            <div className="lm-run-bar"><span className="lm-run-fill" style={{ width: `${progress}%` }} /></div>
            <span className="lm-run-hint">Die Engine recherchiert live und baut bewertete Angebote. Das dauert meist ein bis zwei Minuten, bitte das Fenster offen lassen.</span>
          </div>
        </div>
      )}

      {hasContent && (
        <>
          {(audit.summary || audit.brainBreaker) && (
            <div className="lm-audit-insight">
              {audit.summary && <p className="lm-audit-summary">{audit.summary}</p>}
              {audit.brainBreaker && (
                <div className="lm-brainbreaker">
                  <span className="lm-brainbreaker-label">Brain Breaker · Empfehlung</span>
                  <p>{audit.brainBreaker}</p>
                  {onToggleCart && (
                    <BrainBreakerPromote
                      inCart={cartIds && cartIds.has('bb')}
                      onToggle={() => onToggleCart('bb', 'brainBreaker', audit.brainBreaker)}
                    />
                  )}
                </div>
              )}
            </div>
          )}

          {offers.length > 0 && (
            <>
              <div className="lm-section-head">
                <h4>Offer-Engineering · Einstiegsangebote</h4>
                <div className="lm-section-end">
                  <span className="lm-section-count">{offers.length} bewertet</span>
                  {isAdmin && generateAudit && (
                    <button type="button" className="lm-regen" onClick={() => run(['offers'])} disabled={busy} title="Nur die Angebote neu generieren">
                      {busy && busySection === 'offers' ? <><span className="lm-spin lm-spin-dark" />Läuft…</> : '↻ Angebote neu'}
                    </button>
                  )}
                </div>
              </div>
              <div className="lm-idea-grid">
                {offers.map((o, i) => {
                  const id = `offer:${i}:${o.name || ''}`;
                  return (
                    <OfferCard
                      key={id}
                      offer={o}
                      rank={i + 1}
                      inCart={cartIds && cartIds.has(id)}
                      onToggleCart={onToggleCart ? () => onToggleCart(id, 'offer', o) : null}
                    />
                  );
                })}
              </div>
            </>
          )}

          {hot.length > 0 && (
            <>
              <div className="lm-section-head">
                <h4>Markt-Scan · heiß bei der Konkurrenz</h4>
                <div className="lm-section-end">
                  <span className="lm-section-count">{hot.length} Konzepte</span>
                  {isAdmin && generateAudit && (
                    <button type="button" className="lm-regen" onClick={() => run(['scan'])} disabled={busy} title="Nur den Markt-Scan neu generieren">
                      {busy && busySection === 'scan' ? <><span className="lm-spin lm-spin-dark" />Läuft…</> : '↻ Scan neu'}
                    </button>
                  )}
                </div>
              </div>
              <div className="lm-idea-grid">
                {hot.map((idea, i) => {
                  const id = `idea:${i}:${idea.title || ''}`;
                  return (
                    <HotIdeaCard
                      key={id}
                      idea={idea}
                      rank={i + 1}
                      inCart={cartIds && cartIds.has(id)}
                      onToggleCart={onToggleCart ? () => onToggleCart(id, 'idea', idea) : null}
                    />
                  );
                })}
              </div>
            </>
          )}

          {outdated.length > 0 && (
            <>
              <div className="lm-section-head lm-section-head-muted">
                <h4>Ausgelutscht &amp; überholt</h4>
                <span className="lm-section-count">{outdated.length} vermeiden</span>
              </div>
              <div className="lm-outdated-grid">
                {outdated.map((o, i) => (
                  <div key={i} className="lm-outdated">
                    <span className="lm-outdated-title">{o.title}</span>
                    {o.reason && <span className="lm-outdated-reason">{o.reason}</span>}
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {promptOpen && (
        <div
          className="lm-prompt-overlay"
          role="dialog"
          aria-modal="true"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setPromptOpen(false); }}
        >
          <div className="lm-prompt">
            <button type="button" className="lm-prompt-x" onClick={() => setPromptOpen(false)} aria-label="Schließen">×</button>
            <span className="lm-eyebrow lm-eyebrow-gold">Richtung wählen · steuert die Generierung</span>
            <h3 className="lm-prompt-title">In welche Richtung soll es gehen?</h3>
            <p className="lm-prompt-sub">
              Die KI liest das Research Center und schlägt Richtungen vor. Klick deine Favoriten an –
              die Angebote werden dann gezielt entlang deiner Auswahl gebaut. Ohne Auswahl arbeitet
              die Engine frei mit dem vorhandenen Research.
            </p>

            {generateDirections && (
              <div className="lm-dir">
                <div className="lm-dir-head">
                  <span className="lm-dir-label">
                    Richtungen (KI-Vorschläge){selectedDirs.size > 0 ? ` · ${selectedDirs.size} gewählt` : ''}
                  </span>
                  <button type="button" className="lm-dir-refresh" onClick={loadDirections} disabled={dirBusy}>
                    {dirBusy ? 'Lädt…' : (directions.length ? '↻ Andere vorschlagen' : 'Vorschläge laden')}
                  </button>
                </div>
                {dirErr && <div className="lm-dir-err">{dirErr}</div>}
                {dirBusy && directions.length === 0 && (
                  <div className="lm-dir-loading">
                    <span className="lm-spin lm-spin-dark" />
                    Die KI liest das Research Center und schlägt Richtungen vor…
                  </div>
                )}
                {directions.length > 0 && (
                  <div className="lm-dir-chips">
                    {directions.map((d, i) => (
                      <button
                        key={i}
                        type="button"
                        className={`lm-dir-chip ${selectedDirs.has(i) ? 'is-active' : ''}`}
                        onClick={() => toggleDir(i)}
                      >
                        <span className="lm-dir-chip-title">{d.title}</span>
                        {d.description && <span className="lm-dir-chip-desc">{d.description}</span>}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            <label className="lm-prompt-freelabel">Zusätzliche Vorgabe (optional)</label>
            <textarea
              className="lm-prompt-input"
              value={guidance}
              onChange={(e) => setGuidance(e.target.value)}
              placeholder="z. B. „Fokus auf interaktive Tools/Rechner, kein klassisches PDF-Whitepaper.“"
              rows={3}
            />
            <div className="lm-prompt-actions">
              <button type="button" className="lm-btn lm-btn-ghost" onClick={() => startRun('', false)}>
                Ohne Vorgaben
              </button>
              <button type="button" className="lm-btn lm-btn-gold" onClick={() => startRun(guidance, true)}>
                {selectedDirs.size > 0
                  ? `Mit ${selectedDirs.size} Richtung${selectedDirs.size === 1 ? '' : 'en'} generieren`
                  : 'Generieren'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Edle, live editierbare Magnet-Karte (Admin) ──
// Sieht aus wie die Kundenkarte (MagnetCard), ist aber direkt bearbeitbar – es gibt
// keine getrennte „Bearbeiten"- und „Kundenansicht"-Ebene mehr, sondern genau eine.
const EditableMagnetCard = ({ magnet, onChange, onRemove, uploadAttachment, generateImage, onDone }) => {
  const [tagDraft, setTagDraft] = useState('');
  const [uploading, setUploading] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);
  const [err, setErr] = useState('');
  const [notesOpen, setNotesOpen] = useState(false);
  const previewInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const patch = (p) => onChange({ ...magnet, ...p });

  const addTag = () => {
    const t = tagDraft.trim();
    if (!t) return;
    const tags = Array.isArray(magnet.tags) ? magnet.tags : [];
    if (!tags.includes(t)) patch({ tags: [...tags, t] });
    setTagDraft('');
  };

  const removeTag = (t) => patch({ tags: (magnet.tags || []).filter((x) => x !== t) });

  const handlePreview = async (file) => {
    if (!file || !uploadAttachment) return;
    setUploading(true); setErr('');
    try {
      const att = await uploadAttachment(file);
      patch({ preview: { url: att.url, name: att.name, type: att.type } });
    } catch (e) { setErr(e.message || 'Upload fehlgeschlagen.'); }
    finally { setUploading(false); if (previewInputRef.current) previewInputRef.current.value = ''; }
  };

  const handleFile = async (file) => {
    if (!file || !uploadAttachment) return;
    setUploading(true); setErr('');
    try {
      const att = await uploadAttachment(file);
      const list = Array.isArray(magnet.attachments) ? magnet.attachments : [];
      patch({ attachments: [...list, att] });
    } catch (e) { setErr(e.message || 'Upload fehlgeschlagen.'); }
    finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
  };

  const removeAttachment = (idx) => patch({ attachments: (magnet.attachments || []).filter((_, i) => i !== idx) });

  // KI-Vorschaubild erzeugen: konsistenter Stil (serverseitig fest), Motiv aus
  // Titel/Beschreibung. Ergebnis wird wie ein Upload als preview übernommen.
  const aiReady = !!generateImage && !!((magnet.title || '').trim() || (magnet.description || '').trim());
  const handleAi = async () => {
    if (!generateImage || aiBusy) return;
    setAiBusy(true); setErr('');
    try {
      const att = await generateImage({ title: magnet.title || '', description: magnet.description || '' });
      patch({ preview: { url: att.url, name: att.name, type: att.type || 'image/png', ai: true } });
    } catch (e) { setErr(e.message || 'Bild-Generierung fehlgeschlagen.'); }
    finally { setAiBusy(false); }
  };

  const openPreview = () => previewInputRef.current && previewInputRef.current.click();
  const hasImg = magnet.preview && magnet.preview.url && isImageType(magnet.preview.type);
  const hasPreview = magnet.preview && magnet.preview.url;

  return (
    <article className="lm-magnet lm-magnet-edit">
      <div className="lm-magnet-preview">
        {hasPreview ? (
          hasImg
            ? <img src={magnet.preview.url} alt={magnet.title || 'Vorschau'} />
            : <div className="lm-magnet-preview-fallback"><span>{magnet.preview.name || 'Vorschau-Datei'}</span></div>
        ) : (
          <div className="lm-magnet-preview-empty-wrap">
            <button type="button" className="lm-magnet-preview-empty" onClick={openPreview} disabled={uploading || aiBusy || !uploadAttachment}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="m21 15-5-5L5 21" />
              </svg>
              <span>{uploading ? 'Lädt…' : 'Vorschaubild hinzufügen'}</span>
            </button>
            {generateImage && (
              <button
                type="button"
                className="lm-magnet-ai-btn"
                onClick={handleAi}
                disabled={aiBusy || uploading || !aiReady}
                title={aiReady ? 'Vorschaubild im einheitlichen Stil per KI erzeugen' : 'Titel oder Beschreibung eingeben, dann kann die KI ein Bild erzeugen'}
              >
                {aiBusy ? (
                  <><span className="lm-ai-spin" />Bild wird erstellt…</>
                ) : (
                  <>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8" /></svg>
                    Mit KI erstellen
                  </>
                )}
              </button>
            )}
          </div>
        )}

        <div className="lm-magnet-pa">
          {onDone && (
            <button type="button" className="lm-magnet-pa-btn lm-magnet-pa-done" onClick={onDone} title="Bearbeitung abschließen">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
            </button>
          )}
          {hasPreview && (
            <>
              <button type="button" className="lm-magnet-pa-btn" onClick={openPreview} title="Bild ändern" disabled={uploading}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
              </button>
              <button type="button" className="lm-magnet-pa-btn" onClick={() => patch({ preview: null })} title="Bild entfernen">×</button>
            </>
          )}
          <button type="button" className="lm-magnet-pa-btn lm-magnet-pa-del" onClick={onRemove} title="Magnet entfernen">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18" /><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M6 6v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V6" /></svg>
          </button>
        </div>
        <input ref={previewInputRef} type="file" accept="image/*" hidden onChange={(e) => handlePreview(e.target.files && e.target.files[0])} />
      </div>

      <div className="lm-magnet-body">
        <input
          className="lm-magnet-edit-input lm-magnet-edit-title"
          value={magnet.title || ''}
          placeholder="Titel des Magneten…"
          onChange={(e) => patch({ title: e.target.value })}
        />
        {Array.isArray(magnet.titleVariants) && magnet.titleVariants.length > 1 && (
          <div className="lm-title-variants">
            <span className="lm-title-variants-label">Titel-Vorschläge</span>
            <div className="lm-title-variants-chips">
              {magnet.titleVariants.map((v, i) => (
                <button
                  key={i}
                  type="button"
                  className={`lm-title-variant ${magnet.title === v ? 'is-active' : ''}`}
                  onClick={() => patch({ title: v })}
                  title="Als Titel übernehmen"
                >
                  {v}
                </button>
              ))}
            </div>
          </div>
        )}
        <input
          className="lm-magnet-edit-input lm-magnet-edit-hook"
          value={magnet.hook || ''}
          placeholder="Hook / Einzeiler…"
          onChange={(e) => patch({ hook: e.target.value })}
        />
        <textarea
          className="lm-magnet-edit-input lm-magnet-edit-desc"
          value={magnet.description || ''}
          placeholder="Beschreibung: was der Kunde bekommt und warum es Value liefert…"
          onChange={(e) => patch({ description: e.target.value })}
        />

        <div className="lm-magnet-edit-foundations">
          <span className="lm-magnet-edit-foundations-label">Angebots-Bausteine (für den Kunden sichtbar)</span>
          {((magnet.title || '').trim() || (magnet.description || '').trim()) && magnetFoundationsMissing(magnet).length > 0 && (
            <div className="lm-foundations-hint">
              {magnetFoundationsMissing(magnet).length === 1 ? 'Noch 1 Baustein offen' : `Noch ${magnetFoundationsMissing(magnet).length} Bausteine offen`}: {magnetFoundationsMissing(magnet).map((k) => FOUNDATION_LABEL[k]).join(', ')}
            </div>
          )}
          {[
            { key: 'tangibleResult', ph: 'Greifbares Ergebnis…' },
            { key: 'timeline', ph: 'Zeitrahmen, z. B. „in 14 Tagen“…' },
            { key: 'guarantee', ph: 'Garantie / Risiko-Umkehr…' },
            { key: 'riskReversal', ph: 'Weitere Risiko-Minimierung…' },
          ].map((f) => (
            <input
              key={f.key}
              className="lm-magnet-edit-input lm-magnet-edit-foundation"
              value={(magnet.foundations && magnet.foundations[f.key]) || ''}
              placeholder={f.ph}
              onChange={(e) => patch({ foundations: { ...(magnet.foundations || {}), [f.key]: e.target.value } })}
            />
          ))}
        </div>

        <div className="lm-tags lm-magnet-edit-tags">
          {(magnet.tags || []).map((t) => (
            <span key={t} className="lm-tag">
              {t}<button type="button" onClick={() => removeTag(t)} aria-label="Tag entfernen">×</button>
            </span>
          ))}
          <input
            className="lm-tag-input"
            value={tagDraft}
            placeholder="+ Tag"
            onChange={(e) => setTagDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag(); } }}
          />
        </div>

        <div className="lm-magnet-files lm-magnet-edit-files">
          {(magnet.attachments || []).map((a, i) => (
            <div key={i} className="lm-file-row">
              <a href={a.url} target="_blank" rel="noreferrer" className="lm-file-name">{a.name || 'Datei'}</a>
              <button type="button" className="lm-icon-del lm-icon-del-sm" onClick={() => removeAttachment(i)}>×</button>
            </div>
          ))}
          <button type="button" className="lm-mini-btn" onClick={() => fileInputRef.current && fileInputRef.current.click()} disabled={uploading || !uploadAttachment}>
            {uploading ? 'Lädt…' : '+ Datei hinzufügen'}
          </button>
          <input ref={fileInputRef} type="file" hidden onChange={(e) => handleFile(e.target.files && e.target.files[0])} />
        </div>

        <div className="lm-magnet-notes">
          <button type="button" className="lm-details-toggle" onClick={() => setNotesOpen((v) => !v)} aria-expanded={notesOpen}>
            <span>Interne Team-Notiz</span>
            <svg className={`lm-details-chev ${notesOpen ? 'is-open' : ''}`} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>
          </button>
          {notesOpen && (
            <textarea
              className="lm-magnet-edit-input lm-magnet-edit-notes"
              value={magnet.notes || ''}
              placeholder="Nur fürs Team: Umsetzung, Tools, Wert-zu-Kosten… (nicht für den Kunden sichtbar)"
              onChange={(e) => patch({ notes: e.target.value })}
            />
          )}
        </div>
        {err && <div className="lm-edit-err">{err}</div>}
      </div>
    </article>
  );
};

// ── Strukturierte Offer-Foundations auf der Magnet-Karte (kundenrelevant) ──
// Ergebnis + Zeitrahmen + Garantie + Risiko-Umkehr werden klar getrennt dargestellt,
// damit genau die vertrauensbildenden Bausteine sichtbar sind (statt im Fliesstext).
const MagnetFoundations = ({ foundations }) => {
  const f = foundations || {};
  const hasAny = (f.tangibleResult || '').trim() || (f.timeline || '').trim() || (f.guarantee || '').trim() || (f.riskReversal || '').trim();
  if (!hasAny) return null;
  return (
    <div className="lm-magnet-foundations">
      {(f.timeline || '').trim() && (
        <span className="lm-magnet-foundation-badge">{f.timeline}</span>
      )}
      {(f.tangibleResult || '').trim() && (
        <div className="lm-magnet-foundation">
          <span className="lm-magnet-foundation-label">Dein Ergebnis</span>
          <p>{f.tangibleResult}</p>
        </div>
      )}
      {(f.guarantee || '').trim() && (
        <div className="lm-magnet-foundation lm-magnet-foundation-guarantee">
          <span className="lm-magnet-foundation-label">Garantie</span>
          <p>{f.guarantee}</p>
        </div>
      )}
      {(f.riskReversal || '').trim() && (
        <div className="lm-magnet-foundation">
          <span className="lm-magnet-foundation-label">Ohne Risiko</span>
          <p>{f.riskReversal}</p>
        </div>
      )}
    </div>
  );
};

// ── Eine große Magnet-Karte (Kunden-Ansicht) ──
const MagnetCard = ({ magnet }) => (
  <article className="lm-magnet">
    <div className="lm-magnet-preview">
      {magnet.preview && magnet.preview.url && isImageType(magnet.preview.type) ? (
        <img src={magnet.preview.url} alt={magnet.title || 'Lead Magnet'} />
      ) : (
        <div className="lm-magnet-preview-fallback">
          <svg width="46" height="46" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 2 4 7v10l8 5 8-5V7l-8-5z" /><path d="M12 22V12" /><path d="m4 7 8 5 8-5" />
          </svg>
          <span>{magnet.title || 'Lead Magnet'}</span>
        </div>
      )}
    </div>
    <div className="lm-magnet-body">
      <h4 className="lm-magnet-title">{magnet.title || 'Ohne Titel'}</h4>
      {magnet.hook && <p className="lm-magnet-hook">{magnet.hook}</p>}
      {magnet.description && <p className="lm-magnet-desc">{magnet.description}</p>}
      <MagnetFoundations foundations={magnet.foundations} />
      {Array.isArray(magnet.tags) && magnet.tags.length > 0 && (
        <div className="lm-tags lm-tags-static">
          {magnet.tags.map((t) => <span key={t} className="lm-tag lm-tag-static">{t}</span>)}
        </div>
      )}
      {Array.isArray(magnet.attachments) && magnet.attachments.length > 0 && (
        <div className="lm-magnet-files">
          {magnet.attachments.map((a, i) => (
            <a key={i} href={a.url} target="_blank" rel="noreferrer" className="lm-magnet-file">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
              {a.name || 'Datei'}
            </a>
          ))}
        </div>
      )}
    </div>
  </article>
);

// ── Admin-Board-Karte: standardmaessig die edle Kundenansicht (Titel etc. voll,
// nichts abgeschnitten). Per Edit-Symbol wird live in die Bearbeitung umgeschaltet. ──
const AdminMagnetCard = ({ magnet, onChange, onRemove, uploadAttachment, generateImage }) => {
  const [editing, setEditing] = useState(false);

  if (editing) {
    return (
      <EditableMagnetCard
        magnet={magnet}
        onChange={onChange}
        onRemove={onRemove}
        uploadAttachment={uploadAttachment}
        generateImage={generateImage}
        onDone={() => setEditing(false)}
      />
    );
  }

  const missing = magnetFoundationsMissing(magnet);
  const showMissing = missing.length > 0 && ((magnet.title || '').trim() || (magnet.description || '').trim());

  return (
    <div className="lm-magnet-wrap">
      <button type="button" className="lm-edit-fab" onClick={() => setEditing(true)} title="Magnet bearbeiten">
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9" /><path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
        <span>Bearbeiten</span>
      </button>
      {showMissing && (
        <button
          type="button"
          className="lm-magnet-missing-badge"
          onClick={() => setEditing(true)}
          title={`Fehlende Bausteine: ${missing.map((k) => FOUNDATION_LABEL[k]).join(', ')}. Zum Ergänzen klicken.`}
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>
          {missing.length} {missing.length === 1 ? 'Baustein' : 'Bausteine'}
        </button>
      )}
      <MagnetCard magnet={magnet} />
    </div>
  );
};

// ── Magnet Board: die uebernommenen, kundensichtbaren Magnete ──
const MagnetsView = ({ magnets, setMagnets, uploadAttachment, generateImage, isAdmin, companyName, onGoToLab }) => {
  const updateMagnet = useCallback((id, next) => {
    setMagnets((list) => list.map((m) => (m.id === id ? next : m)));
  }, [setMagnets]);

  const removeMagnet = useCallback((id) => {
    setMagnets((list) => list.filter((m) => m.id !== id));
  }, [setMagnets]);

  const addMagnet = useCallback(() => {
    setMagnets((list) => [...list, { id: makeMagnetId(), title: '', hook: '', description: '', notes: '', type: 'dfy', tags: [], preview: null, attachments: [], createdAt: Date.now() }]);
  }, [setMagnets]);

  const filled = useMemo(() => (magnets || []).filter((m) => m && (m.title || m.description || m.hook || (m.attachments && m.attachments.length) || (m.preview && m.preview.url))), [magnets]);

  if (!isAdmin && filled.length === 0) {
    return (
      <div className="lm-magnets-empty-customer">
        <div className="lm-audit-empty-orb" />
        <h3>Deine Kundenmagnete sind in Arbeit</h3>
        <p>Wir kuratieren gerade die stärksten Lead-Magneten für {companyName || 'dich'}. Sie erscheinen in Kürze genau hier.</p>
      </div>
    );
  }

  // Admin-Board ohne Magnete: sanfter Hinweis, dass die Auswahl aus dem Lab kommt.
  if (isAdmin && (magnets || []).length === 0) {
    return (
      <div className="lm-board">
        <div className="lm-board-head">
          <div className="lm-board-head-text">
            <span className="lm-eyebrow">Magnet Board · Kundenansicht</span>
            <h3 className="lm-board-title">Noch keine Magnete im Board</h3>
          </div>
        </div>
        <div className="lm-board-empty">
          <div className="lm-audit-empty-orb" />
          <p>Erzeuge im <strong>Magnet Lab</strong> Konzepte, lege die stärksten in den Warenkorb und übernimm sie. Danach erscheinen sie hier als kuratierte Kundenansicht, die du noch bearbeiten kannst.</p>
          {onGoToLab && (
            <button type="button" className="lm-btn lm-btn-gold" onClick={onGoToLab}>Zum Magnet Lab</button>
          )}
          <button className="lm-btn lm-btn-ghost lm-add-magnet" onClick={addMagnet} type="button">
            + Magnet manuell anlegen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="lm-board lm-magnets">
      <div className="lm-board-head">
        <div className="lm-board-head-text">
          <span className="lm-eyebrow lm-eyebrow-gold">{isAdmin ? 'Magnet Board · live editierbar' : 'Deine Kundenmagnete'}</span>
          <h3 className="lm-board-title">{isAdmin ? 'Kundenmagnete' : 'Deine Kundenmagnete'}</h3>
        </div>
        <span className="lm-section-count">{filled.length} {filled.length === 1 ? 'Magnet' : 'Magnete'}</span>
      </div>

      {isAdmin ? (
        <>
          <div className="lm-magnet-grid lm-edit-grid">
            {(magnets || []).map((m) => (
              <AdminMagnetCard
                key={m.id}
                magnet={m}
                onChange={(next) => updateMagnet(m.id, next)}
                onRemove={() => removeMagnet(m.id)}
                uploadAttachment={uploadAttachment}
                generateImage={generateImage}
              />
            ))}
          </div>
          <button className="lm-btn lm-btn-ghost lm-add-magnet" onClick={addMagnet} type="button">
            + Neuen Magnet anlegen
          </button>
        </>
      ) : (
        <div className="lm-magnet-grid">
          {filled.map((m) => <MagnetCard key={m.id} magnet={m} />)}
        </div>
      )}
    </div>
  );
};

// ── Builder: ein engineertes Konzept -> Kundenmagnet (Deliverable) ──
// Name/Versprechen/Hook/Typ wandern mit; Cold-Outreach-Angle in die Beschreibung,
// das interne "Wie/Warum" in die Team-Notiz (nur fuer das Team sichtbar).
const buildMagnetFromOffer = (o) => {
  // Beschreibung bleibt Fliesstext (kundentauglich). Die kundenrelevanten Foundations
  // (Ergebnis, Zeitrahmen, Garantie, Risiko-Umkehr) werden STRUKTURIERT gespeichert und
  // auf der Magnet-Karte eigenstaendig gerendert, statt als Textklumpen unterzugehen.
  const desc = [
    o.description,
    o.coldOutreachAngle ? `Cold-Outreach-Angle: ${o.coldOutreachAngle}` : '',
  ].filter(Boolean).join('\n\n');
  const foundations = {
    tangibleResult: o.tangibleResult || '',
    timeline: o.timeline || '',
    guarantee: o.guarantee || '',
    riskReversal: o.riskReversal || '',
  };
  const notes = [
    o.focus ? `Fokus (1 Person, 1 Problem): ${o.focus}` : '',
    o.polarising ? `Polarisierung: ${o.polarising}` : '',
    o.alignment ? `Aligned (beide gewinnen): ${o.alignment}` : '',
    o.clientRequirements ? `Was der Kunde tun muss: ${o.clientRequirements}` : '',
    o.implementation ? `Umsetzung: ${o.implementation}` : '',
    o.rationale ? `Wert-zu-Kosten: ${o.rationale}` : '',
    o.coreFitNote ? `Brücke zur Hauptleistung: ${o.coreFitNote}` : '',
    Array.isArray(o.techTools) && o.techTools.length ? `Tech & KI: ${o.techTools.join(', ')}` : '',
    typeof o.score === 'number' ? `Offer-Score: ${o.score}/100` : '',
  ].filter(Boolean).join('\n\n');
  const titleVariants = Array.isArray(o.nameVariants) && o.nameVariants.length
    ? o.nameVariants.filter(Boolean)
    : (o.name ? [o.name] : []);
  return {
    id: makeMagnetId(),
    title: titleVariants[0] || o.name || '',
    titleVariants,
    hook: o.promise || '',
    description: desc,
    foundations,
    notes,
    type: o.delivery === 'ideas' ? 'ideas' : 'dfy',
    tags: o.format ? [o.format] : [],
    preview: null,
    attachments: [],
    createdAt: Date.now(),
  };
};

// ── Builder: eine Markt-Scan-Idee -> Kundenmagnet ──
const buildMagnetFromIdea = (idea) => {
  const desc = [idea.description, idea.coldOutreachAngle ? `Cold-Outreach-Angle: ${idea.coldOutreachAngle}` : '']
    .filter(Boolean).join('\n\n');
  const notes = [
    idea.whyItWorks ? `Warum es zieht: ${idea.whyItWorks}` : '',
    idea.implementation ? `Umsetzung: ${idea.implementation}` : '',
    Array.isArray(idea.techTools) && idea.techTools.length ? `Tech & KI: ${idea.techTools.join(', ')}` : '',
    idea.region ? `Region: ${REGION_LABEL[idea.region] || idea.region}` : '',
  ].filter(Boolean).join('\n\n');
  const tags = [idea.format, REGION_LABEL[idea.region] || idea.region].filter(Boolean);
  const titleVariants = Array.isArray(idea.titleVariants) && idea.titleVariants.length
    ? idea.titleVariants.filter(Boolean)
    : (idea.title ? [idea.title] : []);
  return {
    id: makeMagnetId(),
    title: titleVariants[0] || idea.title || '',
    titleVariants,
    hook: '',
    description: desc,
    notes,
    type: idea.delivery === 'ideas' ? 'ideas' : 'dfy',
    tags,
    preview: null,
    attachments: [],
    createdAt: Date.now(),
  };
};

// ── Builder: Brain Breaker (Freitext) -> Kundenmagnet-Entwurf ──
const buildMagnetFromBrainBreaker = (text) => ({
  id: makeMagnetId(),
  title: 'Brain Breaker',
  hook: '',
  description: String(text || ''),
  notes: '',
  type: 'dfy',
  tags: ['Brain Breaker'],
  preview: null,
  attachments: [],
  createdAt: Date.now(),
});

const buildMagnetFromCartItem = (item) => {
  if (!item) return null;
  if (item.kind === 'offer') return buildMagnetFromOffer(item.data);
  if (item.kind === 'idea') return buildMagnetFromIdea(item.data);
  if (item.kind === 'brainBreaker') return buildMagnetFromBrainBreaker(item.data);
  return null;
};

// ── Hauptkomponente ──
const LeadMagnetView = ({
  isAdmin = false,
  unlocked = false,
  magnets = [],
  setMagnets,
  audit = null,
  generateAudit,
  generateDirections,
  uploadAttachment,
  generateImage,
  companyName = '',
  customerPsychology = null,
}) => {
  // Gibt es bereits "echte" (befuellte) Magnete im Board? Steuert die Standardansicht.
  const hasMagnets = useMemo(
    () => (Array.isArray(magnets) ? magnets : []).some(
      (m) => m && (m.title || m.description || m.hook || (m.attachments && m.attachments.length) || (m.preview && m.preview.url)),
    ),
    [magnets],
  );

  // Modus: 'lab' (KI-Generierung + Auswahl) vs. 'board' (uebernommene Magnete = Kundenansicht).
  // Standard: sobald Magnete existieren, direkt ins Board, sonst ins Lab.
  const [mode, setMode] = useState(() => (hasMagnets ? 'board' : 'lab'));

  // Warenkorb: ausgewaehlte Konzepte (offer/idea/brainBreaker), erst beim Uebernehmen
  // werden daraus echte Kundenmagnete.
  const [cart, setCart] = useState([]);
  const cartIds = useMemo(() => new Set(cart.map((c) => c.id)), [cart]);

  const toggleCart = useCallback((id, kind, data) => {
    setCart((list) => (list.some((c) => c.id === id)
      ? list.filter((c) => c.id !== id)
      : [...list, { id, kind, data }]));
  }, []);

  // Auswahl uebernehmen: alle Cart-Items in Kundenmagnete wandeln, Cart leeren und
  // automatisch ins Magnet Board springen.
  const adoptCart = useCallback(() => {
    if (!setMagnets || !cart.length) return;
    const built = cart.map(buildMagnetFromCartItem).filter(Boolean);
    if (!built.length) return;
    setMagnets((list) => [...(Array.isArray(list) ? list : []), ...built]);
    setCart([]);
    setMode('board');
  }, [cart, setMagnets]);

  return (
    <div className="lm-wrap">
      <div className="lm-orbs" aria-hidden="true">
        <span className="lm-orb lm-orb-1" />
        <span className="lm-orb lm-orb-2" />
        <span className="lm-orb lm-orb-3" />
        <span className="lm-orb lm-orb-4" />
        <span className="lm-orb lm-orb-5" />
      </div>
      <div className="lm-hero">
        <div className="lm-hero-text">
          <span className="lm-eyebrow lm-eyebrow-gold">Angebote, die anziehen</span>
          <h1 className="lm-hero-title">Kundenmagnet</h1>
        </div>
        {/* Klare Trennung: links das Magnet Lab (KI-Generierung), rechts das
            Magnet Board (uebernommene, kundensichtbare Magnete). Nur fuer das Team. */}
        {isAdmin && (
          <div className="lm-modeswitch" role="tablist" aria-label="Ansicht">
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'lab'}
              className={`lm-modeswitch-btn ${mode === 'lab' ? 'is-active' : ''}`}
              onClick={() => setMode('lab')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M9 3v6.5L4.2 18a2 2 0 0 0 1.8 3h12a2 2 0 0 0 1.8-3L15 9.5V3" /><path d="M8 3h8" /><path d="M7.5 14h9" />
              </svg>
              Magnet Lab
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === 'board'}
              className={`lm-modeswitch-btn ${mode === 'board' ? 'is-active' : ''}`}
              onClick={() => setMode('board')}
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" />
              </svg>
              Magnet Board
              {hasMagnets && <span className="lm-modeswitch-dot" aria-hidden="true" />}
            </button>
          </div>
        )}
      </div>

      <div className="lm-subbody">
        {isAdmin && mode === 'lab' && (
          <>
            <AuditView
              audit={audit}
              generateAudit={generateAudit}
              generateDirections={generateDirections}
              isAdmin={isAdmin}
              unlocked={unlocked}
              cartIds={cartIds}
              onToggleCart={setMagnets ? toggleCart : null}
              customerPsychology={customerPsychology}
            />
            {cart.length > 0 && (
              <div className="lm-cart-bar" role="region" aria-label="Warenkorb">
                <div className="lm-cart-bar-info">
                  <span className="lm-cart-bar-count">{cart.length}</span>
                  <span className="lm-cart-bar-text">
                    {cart.length === 1 ? 'Konzept ausgewählt' : 'Konzepte ausgewählt'}
                  </span>
                </div>
                <div className="lm-cart-bar-actions">
                  <button type="button" className="lm-cart-bar-clear" onClick={() => setCart([])}>
                    Leeren
                  </button>
                  <button type="button" className="lm-btn lm-btn-gold" onClick={adoptCart}>
                    Auswahl ins Magnet Board übernehmen
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {((isAdmin && mode === 'board') || !isAdmin) && (
          <MagnetsView
            magnets={magnets}
            setMagnets={setMagnets}
            uploadAttachment={uploadAttachment}
            generateImage={generateImage}
            isAdmin={isAdmin}
            companyName={companyName}
            onGoToLab={() => setMode('lab')}
          />
        )}
      </div>
    </div>
  );
};

export default LeadMagnetView;
